import { Link, useParams, Navigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft } from "lucide-react";
import { BLOG_POSTS, ABOUT_IMAGE, BRAND } from "@/lib/data";
import { Seo } from "@/components/Seo";

function buildJsonLd(post) {
    const schemas = [
        {
            "@context": "https://schema.org",
            "@type": "BlogPosting",
            headline: `${post.title}: ${post.subtitle}`,
            description: post.seoDescription || post.excerpt,
            image: post.cover,
            datePublished: post.dateISO,
            author: {
                "@type": "Person",
                name: post.author,
                jobTitle: post.authorRole,
                worksFor: { "@type": "Organization", name: BRAND.name },
            },
            publisher: {
                "@type": "Organization",
                name: BRAND.name,
                logo: { "@type": "ImageObject", url: "https://brazierdevelopments.com.au/favicon.png" },
            },
            mainEntityOfPage: `https://brazierdevelopments.com.au/blog/${post.slug}`,
        },
    ];
    if (post.faq?.length) {
        schemas.push({
            "@context": "https://schema.org",
            "@type": "FAQPage",
            mainEntity: post.faq.map((f) => ({
                "@type": "Question",
                name: f.q,
                acceptedAnswer: { "@type": "Answer", text: f.a },
            })),
        });
    }
    return schemas;
}

export default function BlogPost() {
    const { slug } = useParams();
    const post = BLOG_POSTS.find((p) => p.slug === slug);
    if (!post) return <Navigate to="/blog" replace />;

    const firstParaIdx = post.content.findIndex((b) => b.type === "p");

    return (
        <main data-testid="blog-post-page" className="bg-bone">
            <Seo
                title={`${post.title} | Brazier Building & Design`}
                description={post.seoDescription || post.excerpt}
                path={`/blog/${post.slug}`}
                image={post.cover}
                type="article"
                jsonLd={buildJsonLd(post)}
            />
            {/* Article header */}
            <header className="pt-16 md:pt-24 pb-10 md:pb-14">
                <div className="container-wide">
                    <Link
                        to="/blog"
                        data-testid="blog-back-link"
                        className="inline-flex items-center gap-2 text-xs tracking-[0.2em] uppercase text-ink2 hover:text-clay transition-colors"
                    >
                        <ArrowLeft size={14} /> The Journal
                    </Link>

                    <div className="mt-10 max-w-4xl mx-auto text-center">
                        <div className="flex items-center justify-center gap-3 text-[11px] tracking-[0.25em] uppercase">
                            <span className="w-8 h-px bg-clay" />
                            <span className="text-clay font-medium">{post.category}</span>
                            <span className="w-8 h-px bg-clay" />
                        </div>
                        <motion.h1
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.8 }}
                            className="mt-6 font-display text-4xl md:text-6xl font-light tracking-tight leading-[1.08]"
                        >
                            {post.title}
                        </motion.h1>
                        <p className="mt-4 font-display italic text-clay text-xl md:text-2xl font-light">
                            {post.subtitle}
                        </p>
                        <div className="mt-8 flex items-center justify-center gap-4 text-xs tracking-[0.18em] uppercase text-ink2">
                            <span>By {post.author}</span>
                            <span className="w-1 h-1 rounded-full bg-clay" />
                            <span>{post.date}</span>
                            <span className="w-1 h-1 rounded-full bg-clay" />
                            <span>{post.readTime}</span>
                        </div>
                    </div>
                </div>
            </header>

            {/* Cover */}
            <div className="container-wide">
                <motion.figure
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ duration: 1 }}
                    className="max-w-5xl mx-auto"
                >
                    <div className="aspect-[16/9] overflow-hidden bg-bone2">
                        <img src={post.cover} alt={post.coverAlt} className="w-full h-full object-cover" />
                    </div>
                    <figcaption className="mt-3 text-center text-xs italic text-ink2">
                        {post.coverAlt}
                    </figcaption>
                </motion.figure>
            </div>

            {/* Body */}
            <article className="py-14 md:py-20">
                <div className="container-wide">
                    <div className="max-w-2xl mx-auto">
                        {post.content.map((block, i) => (
                            <Block key={i} block={block} dropcap={i === firstParaIdx} />
                        ))}

                        {/* FAQ */}
                        {post.faq?.length > 0 && (
                            <section className="mt-16 pt-10 border-t border-line" data-testid="blog-post-faq">
                                <div className="flex items-center gap-4 mb-8">
                                    <span className="w-10 h-px bg-clay" />
                                    <h2 className="overline">Common Questions</h2>
                                </div>
                                <div className="space-y-8">
                                    {post.faq.map((f) => (
                                        <div key={f.q}>
                                            <h3 className="font-display text-lg md:text-xl font-medium tracking-tight">{f.q}</h3>
                                            <p className="mt-3 text-ink2 leading-relaxed">{f.a}</p>
                                        </div>
                                    ))}
                                </div>
                            </section>
                        )}

                        {/* Author sign-off */}
                        <div className="mt-16 pt-10 border-t border-line flex items-center gap-5">
                            <img
                                src={ABOUT_IMAGE}
                                alt={post.author}
                                className="w-16 h-16 rounded-full object-cover"
                            />
                            <div>
                                <div className="font-display text-lg font-medium">{post.author}</div>
                                <div className="text-xs tracking-[0.18em] uppercase text-ink2 mt-1">{post.authorRole}</div>
                            </div>
                        </div>

                        {/* CTA */}
                        <div className="mt-14 bg-char text-bone p-10 md:p-12 text-center">
                            <div className="overline text-clay mb-4">Start the conversation</div>
                            <h3 className="font-display text-2xl md:text-3xl font-light tracking-tight">
                                Ask me the hard questions. <span className="italic text-clay">I welcome them.</span>
                            </h3>
                            <Link to="/contact" data-testid="blog-post-contact-cta" className="btn-primary mt-8 inline-flex">
                                Get In Touch
                            </Link>
                        </div>
                    </div>
                </div>
            </article>
        </main>
    );
}

function renderText(text) {
    return text.split(/(\[[^\]]+\]\([^)]+\))/g).map((part, i) => {
        const m = part.match(/^\[([^\]]+)\]\(([^)]+)\)$/);
        if (m) {
            return (
                <Link key={i} to={m[2]} className="text-clay underline underline-offset-4 decoration-clay/40 hover:decoration-clay transition-colors">
                    {m[1]}
                </Link>
            );
        }
        return part;
    });
}

function Block({ block, dropcap }) {
    if (block.type === "h2") {
        return (
            <h2 className="mt-14 mb-6 font-display text-2xl md:text-3xl font-light tracking-tight flex items-baseline gap-4">
                <span className="w-8 h-px bg-clay shrink-0 translate-y-[-6px]" />
                {block.text}
            </h2>
        );
    }
    if (block.type === "quote") {
        return (
            <blockquote className="my-12 text-center px-2 md:px-8">
                <div className="font-display text-clay text-6xl leading-none select-none" aria-hidden="true">“</div>
                <p className="font-display italic text-2xl md:text-3xl font-light leading-snug text-ink -mt-4">
                    {block.text}
                </p>
                <div className="mt-6 mx-auto w-10 h-px bg-clay" />
            </blockquote>
        );
    }
    if (block.type === "image") {
        return (
            <figure className="my-12 -mx-4 md:-mx-16">
                <div className="aspect-[3/2] overflow-hidden bg-bone2">
                    <img src={block.src} alt={block.alt} loading="lazy" className="w-full h-full object-cover" />
                </div>
                <figcaption className="mt-3 text-center text-xs italic text-ink2 px-4">
                    {block.caption}
                </figcaption>
            </figure>
        );
    }
    if (block.type === "list") {
        return (
            <ul className="my-8 space-y-4">
                {block.items.map((item) => (
                    <li key={item} className="flex items-start gap-4 text-ink2 leading-relaxed">
                        <span className="mt-[0.7em] w-5 h-px bg-clay shrink-0" />
                        <span>
                            <strong className="text-ink font-medium">{item.split(".")[0]}.</strong>
                            {item.slice(item.indexOf(".") + 1)}
                        </span>
                    </li>
                ))}
            </ul>
        );
    }
    return (
        <p className={`my-6 text-ink2 leading-[1.85] text-[17px] ${dropcap ? "dropcap" : ""}`}>
            {renderText(block.text)}
        </p>
    );
}
